import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, RowDataUpdatedEvent } from 'ag-grid-community';
import { MessageModalComponent } from '@niceltd/cxone-components/modal';
import { ToastrManager } from '@niceltd/cxone-components/toastr';
import { DialogService } from 'primeng/dynamicdialog';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CustomNoRowsOverlayComponent } from '../../shared/custom-no-rows-overlay/custom-no-rows-overlay.component';
import { CxoneEtlLoadFromFileComponent } from '../create-edit-data-feed/cxone-etl-load-from-file/cxone-etl-load-from-file.component';
import { ETLDataFeedObj } from '../cxone-etl-data-feed.model';
import { CxoneEtlDataFeedService } from '../cxone-etl-data-feed.service';
import { ActionRendererComponent } from './cell-renderers/action-renderer/action-renderer.component';
import { ActiveRendererComponent } from './cell-renderers/active-renderer/active-renderer.component';
import { DataTypeRendererComponent } from './cell-renderers/data-type-renderer/data-type-renderer.component';
import { FieldNameRendererComponent } from './cell-renderers/field-name-renderer/field-name-renderer.component';
import { PrimaryKeyHeaderComponent } from './cell-renderers/primary-key-header/primary-key-header.component';
import { PrimaryKeyRendererComponent } from './cell-renderers/primary-key-renderer/primary-key-renderer.component';
import { DataRows, DataTypeDropdownOptions, SaveDataFeedDefinition } from './create-edit-data-feed.model';
import { DataFieldsEntity } from './dataFields';

@Component({
    selector: 'cv-create-edit-data-feed',
    templateUrl: './create-edit-data-feed.component.html',
    styleUrls: ['./create-edit-data-feed.component.scss'],
    providers: [DialogService]
})

export class CreateEditEtlDataFeedComponent implements OnInit {
    @Output() backAction: EventEmitter<void> = new EventEmitter();
    @Input() dataFeedGridData: ETLDataFeedObj[];
    @Input() isEditable = false;
    @Input() isPreview: boolean;
    @Input() selectedDataFeed: ETLDataFeedObj;

    feedPlaceholder = '';
    sourcePlaceholder = '';
    searchPlaceholder = '';
    fieldsDataRowCount = '';
    addButtonDisable = false;
    gridOptions: GridOptions;
    feed_name: FormGroup;
    source_name: FormGroup;
    rowData: DataRows[] = [];
    filteredRowData: DataRows[] = [];
    feedName: string;
    sourceName: string;
    dataFeedName: string;
    dataSourceName: string;
    public isFormValid = false;
    public gridApi: GridApi;
    public isLocalizationSectionLoaded: boolean;
    public isGridValid = false;
    public searchInput = '';
    public isSaving = false;
    containsOnlyNumbers = false;
    containsSpecialChars = false;
    isNameAlreadyExists = false;
    isFeedNameAlreadyExists = false;
    fieldTypeMapping: { [key: string]: number } = {};
    invalidKeys: string[] = [];
    primaryKeys: string[] = [];
    fieldTypeValues: DataTypeDropdownOptions[] = [
        { id: 0, name: 'String' },
        { id: 1, name: 'Int' },
        { id: 2, name: 'Double' },
        { id: 3, name: 'DateTime' },
        { id: 4, name: 'Boolean' },
    ];

    objectIdCounter = 0;
    showError = false;
    invalidFieldNameFormat = false;
    primaryKeyNotSelected = false;
    invalidFieldNameFormatMsg: string;
    primaryKeyNotSelectedMsg: string;
    disableSaveBtn = true;
    addNewField = false;

    columnDefs: Array<ColDef>;
    backupEditableRecord: DataFieldsEntity = this.getDataFieldEntity();
    sourceNameList: { value: string; name: string }[];
    sourceNameMap: Map<string, string>;
    selectedSource: { value: string; name: string };
    isFeedEdited = false;
    hasNonSavedChanges = false;
    selectedText: string;

    constructor(fb: FormBuilder, private cvLocalizationService: CVLocalizationService,
        private cxoneEtlDataFeedService: CxoneEtlDataFeedService,
        private toastrManager: ToastrManager,
        private _dialogService: DialogService
    ) {
        this.feed_name = fb.group({
            feedName: [
                { value: null, disabled: false },
                Validators.compose([Validators.required, Validators.maxLength(50),
                this.customValidatorForNumericCharacters.bind(this),
                this.customValidatorForSpecialCharacters.bind(this),
                this.customValidatorForDuplicateFeedName.bind(this)
                ])
            ]
        });
        this.source_name = fb.group({
            sourceName: [
                null,
                Validators.compose([Validators.required,
                this.customValidatorForMaxLengthInSource.bind(this),
                this.customValidatorForNumericCharsInSource.bind(this),
                this.customValidatorForSpecialCharsInSource.bind(this),
                this.customValidatorForDuplicateFeedForSource.bind(this)])
            ]
        });
        this.sourceNameMap = new Map<string, string>();
    }

    async ngOnInit(): Promise<void> {
        await this.loadLocalization();
        this.isLocalizationSectionLoaded = true;
        if (this.dataFeedGridData.length > 0)
            await this.fillSourceNameDropdown();
        this.initializedataFieldsGrid();
    }

    public async fillSourceNameDropdown() {
        this.dataFeedGridData.forEach((dataFeedRow) => {
            this.sourceNameMap.set(dataFeedRow.SourceSystem, dataFeedRow.SourceSystem);
        });
        this.sourceNameList = [];
        this.sourceNameMap.forEach((source, id) => {
            this.sourceNameList.push({ value: id, name: source });
        });
    }

    dropdownOpened() {
        this.source_name.get('sourceName')?.markAsTouched();
    }

    public updateRowData() {
        if (this.isEditable && this.selectedDataFeed) {
            this.feed_name.get('feedName')?.disable();
            this.source_name.get('sourceName')?.disable();
            this.dataFeedName = this.selectedDataFeed.FeedName;
            this.dataSourceName = this.selectedDataFeed.SourceSystem;
            if (this.sourceNameMap.has(this.dataSourceName)) {
                const source = this.sourceNameMap.get(this.dataSourceName);
                this.selectedSource = {
                    value: source,
                    name: source
                };
            }
            this.selectedDataFeed.DataList.forEach((item) => {
                const entity = new DataFieldsEntity();
                if (this.validFieldGrid(item)) {
                    entity.fieldName = item.Name;
                    entity.dataType = item.DataType;
                    entity.isPrimaryKey = item.isKey;
                    entity.isActive = item.isActive;
                    entity.fieldNameValid = true;
                }
                entity.objectId = 'objectId-' + this.objectIdCounter;
                this.objectIdCounter++;
                this.gridApi.applyTransaction({ add: [entity], addIndex: this.gridApi.getDisplayedRowCount() + 1 });
                this.rowData.splice(this.rowData.length, 0, entity);
                this.filteredRowData = [...this.rowData];
                this.resetFieldsGrid();
                this.fieldsDataRowCount = this.gridRowsCountLabel(this.gridApi.getDisplayedRowCount());
            });
            this.isGridValid = true;
            this.isFormValid = true;
        }
    }

    public async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('ETL_Data_Feed_Create');
        }
        catch {
            console.log('Failed to load localization for ETL_Data_Feed_Create');
        }
        finally {
            this.feedPlaceholder = this.getLocalizationString("Add_Feed_Name", "Add feed name");
            this.sourcePlaceholder = this.getLocalizationString("Add_Source_Name", "Add source name");
            this.searchPlaceholder = this.getLocalizationString("Search", "Search fields");
            this.invalidFieldNameFormatMsg = this.getLocalizationString("Invalid_Field_Name",
                "Update some of the field names to have valid format.");
            this.primaryKeyNotSelectedMsg = this.getLocalizationString("Primary_Key_Not_Selected",
                "Data feed cannot be used without a primary key. Select at least one field as a primary key.");
        }
    }

    public initializedataFieldsGrid() {
        this.gridOptions = {
            getRowHeight: (params) => params.node.data.editable ? 70 : 44,
            columnDefs: this.getColumnDefs(),
            components: {
                CustomNoRowsOverlayComponent
            },
            noRowsOverlayComponent: 'CustomNoRowsOverlayComponent',
            noRowsOverlayComponentParams: {
                noRowsOverlayTextFunc: () => this.searchInput !== '' ?
                    this.getLocalizationString("No_Result_Found", "No result found")
                    : this.getLocalizationString("No_Field_Added", "No field added yet"),
                noRowsOverlayIconPathFunc: () => {
                    const path = this.searchInput !== '' ?
                        '../App_Themes/CXone/icons/no-results.svg#no-results'
                        : '../App_Themes/CXone/icons/empty-state.svg#empty-state';
                    return path;
                }
            },
            headerHeight: 45,
            suppressCellFocus: true,
            suppressRowClickSelection: true,
            scrollbarWidth: 8,
            onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
            context: {
                componentParent: this
            },
            onRowDataUpdated: (event: RowDataUpdatedEvent) => {
                if (this.addNewField) {
                    this.insertField();
                    this.addNewField = false;
                }
                event.api.sizeColumnsToFit();
            }
        };
    }

    public onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;
        this.filteredRowData = [...this.rowData];
        this.fieldsDataRowCount = this.gridRowsCountLabel(event.api.getDisplayedRowCount());
        if (this.isEditable) {
            this.updateRowData();
        }
    }

    public validateForm(): void {
        if (!this.isEditable) {
            this.isFormValid = this.feed_name.valid && this.source_name.valid && !this.isFeedNameAlreadyExists;
        }
        if ((this.feed_name.dirty || this.source_name.dirty) && !this.hasNonSavedChanges) {
            this.hasNonSavedChanges = true;
            sendMessageCXOne({ hasNonSavedChanges: true });
        }
    }

    public gridRowsCountLabel(gridRowsCount: number): string {
        const key: string = gridRowsCount === 1 ? 'Total_Records_Singular' : 'Total_Records_Plural';
        const defaultValue: string = gridRowsCount === 1 ? '{0} field' : '{0} fields';
        return this.cvLocalizationService.Format(this.getLocalizationString(key, defaultValue), gridRowsCount);
    }

    public getColumnDefs() {
        let columnDefs = [
            {
                headerName: this.getLocalizationString("Primary_Key", "Primary Key"),
                field: 'isPrimaryKey',
                width: 130,
                headerComponent: PrimaryKeyHeaderComponent,
                cellRenderer: PrimaryKeyRendererComponent,
                cellStyle: { display: 'flex' },
                sortable: false,
                suppressMovable: true,
                suppressSizeToFit: false,
                resizable: false
            },
            {
                headerName: this.getLocalizationString("Field_Name", "Field Name"),
                field: 'fieldName',
                cellRenderer: FieldNameRendererComponent,
                sortable: false,
                suppressMovable: true,
                suppressSizeToFit: false,
                resizable: false
            },
            {
                headerName: this.getLocalizationString("Data_Type", "Data Type"),
                field: 'dataType',
                cellRenderer: DataTypeRendererComponent,
                cellStyle: { 'justify-content': 'flex-start', display: 'flex' },
                sortable: false,
                suppressMovable: true,
                suppressSizeToFit: false,
                resizable: false
            },
            {
                headerName: this.getLocalizationString("Active", "Active"),
                field: 'isActive',
                cellRenderer: ActiveRendererComponent,
                width: 80,
                cellStyle: { 'justify-content': 'flex-start', display: 'flex' },
                sortable: false,
                suppressMovable: true,
                suppressSizeToFit: false,
                resizable: false
            },
            {
                headerName: '',
                field: 'actions',
                cellRenderer: ActionRendererComponent,
                cellStyle: { 'justify-content': 'center', display: 'flex' },
                minWidth: 220,
                sortable: false,
                suppressMovable: true,
                suppressSizeToFit: true,
                resizable: false,
                hide: this.isPreview
            }
        ];
        return columnDefs;
    }

    public openFileUpload() {
        const dialogRef = this._dialogService.open(CxoneEtlLoadFromFileComponent, {
            width: "540px",
            height: "335px",
            styleClass: 'cx-one-ui',
        });
        dialogRef.onClose.subscribe((data) => {
            if (data) {
                this.updateFieldsGrid(data);
                this.toastrManager.showToaster('success', this.getLocalizationString('Data_Loaded', 'Data loaded successfully'));
            }
        });
    }

    public updateFieldsGrid(data) {
        if (data) {
            if (!this.isEditable) {
                this.feed_name.setValue({ feedName: data.FeedName });
                this.source_name.setValue({ sourceName: data.SourceSystem });
                if (this.feed_name.controls.feedName.errors || this.source_name.controls.sourceName.errors) {
                    this.showError = true;
                } else {
                    this.showError = false;
                }
            }
            if (this.source_name.status != 'DISABLED') {
                this.selectedSource = {
                    value: this.source_name.value.sourceName,
                    name: this.source_name.value.sourceName
                };
                this.dataSourceName = this.source_name.value.sourceName;
            }
            if (data.action === 'add') {
                data.dataItems.forEach(dataItem => {
                    const foundItem = this.rowData.find(item => dataItem.name.toUpperCase() === item.fieldName.toUpperCase());
                    if (!foundItem) {
                        const entity = new DataFieldsEntity();
                        entity.fieldName = dataItem.name.toUpperCase();
                        entity.dataType = dataItem.dataType;
                        entity.isPrimaryKey = false;
                        entity.isActive = true;
                        entity.fieldNameValid = true;
                        entity.objectId = 'objectId-' + this.objectIdCounter;
                        this.objectIdCounter++;
                        this.gridApi.applyTransaction({ add: [entity], addIndex: this.gridApi.getDisplayedRowCount() + 1 });
                        this.rowData.splice(this.rowData.length, 0, entity);
                        this.filteredRowData = [...this.rowData];
                        this.resetFieldsGrid();
                        this.fieldsDataRowCount = this.gridRowsCountLabel(this.gridApi.getDisplayedRowCount());
                    }
                });
            } else {
                data.dataItems.forEach(dataItem => {
                    this.rowData.forEach(item => {
                        if (item.fieldName.toUpperCase() === dataItem.name.toUpperCase()) {
                            item.fieldName = dataItem.name.toUpperCase();
                            item.dataType = dataItem.dataType;
                            item.isPrimaryKey = false;
                            item.isActive = true;
                            item.fieldNameValid = true;
                        }
                    });
                    this.filteredRowData = [...this.rowData];
                    this.fieldsDataRowCount = this.gridRowsCountLabel(this.gridApi.getDisplayedRowCount());
                });
            }
        }
        if (this.rowData.length > 0) {
            this.isGridValid = true;
            this.disableSaveBtn = false;
            this.addButtonDisable = false;
        }
        this.validateForm();
    }

    public checkFieldNameFormat() {
        const invalidNameAvailable = this.rowData.filter(item => !item.fieldNameValid);
        this.invalidFieldNameFormat = invalidNameAvailable.length > 0 ? true : false;
    }

    public saveEditing(fieldsGrid: ICellRendererParams) {
        if (fieldsGrid.node.data.objectId === '') {
            this.addFields(fieldsGrid);
        } else {
            this.updateField(fieldsGrid);
        }
        this.fieldsDataRowCount = this.getDisplayedGridRowsCount();
        this.disableEdit(false);
        this.checkFieldNameFormat();
        this.isFeedEdited = true;
        this.disableSaveBtn = false;
    }

    public cancelEditing(fieldsGrid: ICellRendererParams) {
        if (this.validFieldGrid(fieldsGrid) && this.validGridOptions()) {
            if (this.isEmptyObjectId(fieldsGrid)) {
                this.gridApi.applyTransaction({ remove: [fieldsGrid.node.data] });
            } else {
                fieldsGrid.node.data = JSON.parse(JSON.stringify(this.backupEditableRecord));
            }
            this.disableEdit(false);
            this.resetRowData(fieldsGrid);
            fieldsGrid.data.editable = false;
            this.updateRow(fieldsGrid);
            this.fieldsDataRowCount = this.getDisplayedGridRowsCount();
            this.isGridValid = true;
        }
        if (this.rowData.length === 0) {
            this.isGridValid = false;
        }
        this.addButtonDisable = false;
        this.disableSaveBtn = (!this.isFeedEdited && this.disableSaveBtn) ? true : false;
        this.gridApi.sizeColumnsToFit();
    }

    public editRowData(fieldGrid: ICellRendererParams) {
        if (this.allowEdit() && this.validFieldGrid(fieldGrid)) {
            this.isGridValid = false;
            this.backupEditableRecord = this.getDataFieldEntity();
            this.backupEditableRecord = JSON.parse(JSON.stringify(fieldGrid.node.data));
            fieldGrid.node.data.editable = true;
            this.updateRow(fieldGrid);
            this.disableEdit(true);
            this.resetFieldsGrid();
            this.gridApi.sizeColumnsToFit();
        }
    }

    public disableEdit(editFlag: boolean, isFromInsert: boolean = false) {
        if (this.searchInput) {
            const editSearchedData = [...this.filteredRowData];
            if (editSearchedData.length > 0) {
                editSearchedData.forEach((item) => {
                    item.disableEditBtn = editFlag;
                });

                if (isFromInsert) {
                    this.filteredRowData = [...editSearchedData];

                } else {
                    // Assined the new data without changing the reference of filteredRowData array
                    this.filteredRowData.length = 0;
                    Array.prototype.push.apply(this.filteredRowData, editSearchedData);
                }
            }
        }
        else {
            if (this.rowData.length > 0) {
                this.rowData.forEach((item) => {
                    item.disableEditBtn = editFlag;
                });
                if (isFromInsert) {
                    this.filteredRowData = [...this.rowData];
                } else {
                    // Assined the new data without changing the reference of filteredRowData array
                    this.filteredRowData.length = 0;
                    Array.prototype.push.apply(this.filteredRowData, this.rowData);
                }
            }
        }
    }

    public deleteRow(fieldsGrid: ICellRendererParams) {
        this.gridApi.applyTransaction({ remove: [fieldsGrid.node.data] });
        const indexToRemove = this.rowData.findIndex(item => item.objectId === fieldsGrid.node.data.objectId);
        if (indexToRemove !== -1) {
            this.rowData.splice(indexToRemove, 1);
            this.filteredRowData = [...this.rowData];
        }
        this.fieldsDataRowCount = this.gridRowsCountLabel(this.filteredRowData.length);
        const editableRowAvailable = this.rowData.filter((row) => row.editable);
        editableRowAvailable.length > 0 ? this.disableEdit(true) : this.disableEdit(false);
        this.resetFieldsGrid();
        if (this.rowData.length === 0) {
            this.isGridValid = false;
        }
        this.addButtonDisable = false;
        this.checkFieldNameFormat();
        this.disableSaveBtn = false;
        this.hasNonSavedChanges = true;
        sendMessageCXOne({ hasNonSavedChanges: true });
    }

    public addFields(fieldsGrid: ICellRendererParams) {
        if (this.validFieldGrid(fieldsGrid) && this.validGridOptions()) {
            if (this.invalidFieldData(fieldsGrid)) {
                return false;
            }
            let deletedItemCount = 0;
            const dataFields = this.createDataFieldsEntity(fieldsGrid);
            dataFields.objectId = 'objectId-' + this.objectIdCounter;
            this.objectIdCounter++;
            this.gridApi.forEachNode((node) => {
                if (node.data.objectId === '' && deletedItemCount === 0) {
                    this.gridApi.applyTransaction({ remove: [node.data] });
                    deletedItemCount++;
                }
            });

            this.addButtonDisable = false;
            dataFields.editable = false;
            this.gridApi.applyTransaction({ add: [dataFields], addIndex: this.gridApi.getDisplayedRowCount() + 1 });
            this.rowData.splice(this.rowData.length, 0, dataFields);
            this.filteredRowData = [...this.rowData];
            this.resetFieldsGrid();
            this.isGridValid = true;
        }
    }

    public updateField(fieldGrid: ICellRendererParams) {
        if (this.validFieldGrid(fieldGrid)) {
            if (this.invalidFieldData(fieldGrid)) {
                return false;
            }
            this.resetRowData(fieldGrid);
            this.updateRow(fieldGrid);
            this.isGridValid = true;
        }
    }

    public updateRow(fieldGrid: ICellRendererParams) {
        if (this.validFieldGrid(fieldGrid)) {
            this.rowData.forEach((item) => {
                if (item.objectId === fieldGrid.node.data.objectId) {
                    item.editable = fieldGrid.node.data.editable;
                    item.fieldName = fieldGrid.node.data.fieldName;
                    item.dataType = fieldGrid.node.data.dataType;
                    item.isActive = fieldGrid.node.data.isActive;
                    item.isPrimaryKey = fieldGrid.node.data.isPrimaryKey;
                    item.fieldNameValid = fieldGrid.node.data.fieldNameValid;
                }
            });
        }
    }

    public async createUpdateDataFeed() {
        this.isSaving = true;
        if (this.isFormValid) {
            this.extractData();
            if (this.primaryKeys.length === 0) {
                this.primaryKeyNotSelected = true;
            } else {
                this.primaryKeyNotSelected = false;
                await this.saveDataFeed();
            }
        }
        this.isSaving = false;
        this.hasNonSavedChanges = false;
        sendMessageCXOne({ hasNonSavedChanges: false });
    }

    public async saveDataFeed() {
        const dataFeed: SaveDataFeedDefinition = {
            IsEditable: true,
            IsViewable: true,
            IsTemplate: false,
            SourceSystem: this.dataSourceName,
            FeedName: this.dataFeedName,
            SKDefinitionID: null,
            UpdateExistingReportingSets: false,
            UpdateExistingReportingSetsWithinDays: 0,
            QueuedOnImport: true,
            AlwaysDelta: false,
            Mode: 0,
            PrimaryKeyElements: this.primaryKeys,
            FieldTypeMapping: this.fieldTypeMapping,
            InactiveFields: this.invalidKeys,
            IndexedKeys: []
        };
        try {
            await this.cxoneEtlDataFeedService.saveDataFeed(dataFeed);
            if (!this.isEditable) {
                this.toastrManager.showToaster('success', this.getLocalizationString('New_feed', 'New Feed added succesfully.'));
            } else {
                this.toastrManager.showToaster('success', this.getLocalizationString('Update_feed', 'Data Feed updated successfully.'));
            }
            this.backAction.emit(null);
        } catch (error) {
            // PipelineValidationError = 7
            if (error.data == 7) {
                this.toastrManager.showToaster('error',
                    this.getLocalizationString('Pipeline_Validation_Error', 'Couldn\'t edit as currently in use by reporting set.'));
            }
            else {
                this.toastrManager.showToaster('error',
                    this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
            }
            this.backAction.emit(null);
        } finally {
            this.searchInput = '';
        }
    }

    public extractData() {
        this.fieldTypeMapping = {};
        this.invalidKeys = [];
        this.primaryKeys = [];
        if (this.rowData.length > 0) {
            this.rowData.forEach((item) => {
                if (item.isActive === true) {
                    this.fieldTypeMapping[item.fieldName] = item.dataType;
                }
                if (item.isPrimaryKey === true) {
                    this.primaryKeys.push(item.fieldName);
                }
                if (item.isActive === false) {
                    this.invalidKeys.push(item.fieldName);
                }
            });
        }
    }

    public createDataFieldsEntity(fieldGrid: ICellRendererParams) {
        const entity = new DataFieldsEntity();
        if (this.validFieldGrid(fieldGrid)) {
            entity.objectId = fieldGrid.node.data.objectId;
            entity.fieldName = fieldGrid.node.data.fieldName;
            entity.dataType = fieldGrid.node.data.dataType;
            entity.isPrimaryKey = fieldGrid.node.data.isPrimaryKey;
            entity.isActive = fieldGrid.node.data.isActive;
            entity.fieldNameValid = fieldGrid.node.data.fieldNameValid;
        }
        return entity;
    }

    public customValidatorForNumericCharacters(control: FormControl) {
        if (control.value) {
            if (this.checkForNumericCharacters(control.value)) {
                return { numericPattern: true };
            }
        }
        return null;
    }

    public customValidatorForMaxLengthInSource(control: FormControl) {
        if (control.value != null) {
            if (typeof (control.value) === 'object') {
                if (control.value.value.length > 50) {
                    return { maxlength: true };
                }
            }
            else {
                if (control.value.length > 50) {
                    return { maxlength: true };
                }
            }
        }
        return null;
    }

    public customValidatorForNumericCharsInSource(control: FormControl) {
        if (control.value) {
            if (typeof (control.value) === 'object') {
                if (this.checkForNumericCharacters(control.value.value)) {
                    return { numericPattern: true };
                }
            }
            else {
                if (this.checkForNumericCharacters(control.value)) {
                    return { numericPattern: true };
                }
            }
        }
        return null;
    }

    public customValidatorForDuplicateFeedForSource() {
        let feedControlName = null;
        if (this.feed_name.get('feedName').value) {
            feedControlName = this.feed_name.get('feedName').value;
        }
        if (feedControlName)
            this.customValidatorForDuplicateFeedName(feedControlName);
    }

    public customValidatorForSpecialCharsInSource(control: FormControl) {
        if (control.value) {
            if (typeof (control.value) === 'object') {
                if (this.checkForSpecialCharacters(control.value.value)) {
                    return { pattern: true };
                }
            }
            else {
                if (this.checkForSpecialCharacters(control.value)) {
                    return { pattern: true };
                }
            }
        }
        return null;
    }

    public customValidatorForDuplicateFeedName(control: any) {
        if (typeof control === 'object') {
            if (control.value) {
                if (this.checkForDuplicateFeedName(control.value)) {
                    return { duplicateName: true };
                }
            }
        }
        else {
            if (control) {
                if (this.checkForDuplicateFeedName(control)) {
                    return { duplicateName: true };
                }
                else {
                    this.feed_name.get('feedName').setValue(control);
                }
            }
        }
        return null;
    }

    public customValidatorForSpecialCharacters(control: FormControl) {
        if (control.value) {
            if (this.checkForSpecialCharacters(control.value)) {
                return { pattern: true };
            }
        }
        return null;
    }

    public checkForSpecialCharacters(attributeValue: string) {
        const specialCharacters = /[&><[\]^?*,"+\\/!]/;
        if (specialCharacters.test(attributeValue) || /[^\u0000-\u007F]/.test(attributeValue)) {
            this.containsSpecialChars = true;
        }
        else {
            this.containsSpecialChars = false;
        }
        return this.containsSpecialChars;
    }

    public checkForDuplicateFeedName(attributeValue: string) {
        let successCount = 0;
        let sourceControlName = null;
        if (this.source_name.get('sourceName').value) {
            sourceControlName = this.source_name.get('sourceName').value;
        }
        if (sourceControlName) {
            if (typeof sourceControlName === 'object') {
                sourceControlName = sourceControlName.value.toLocaleUpperCase();
            }
            else {
                sourceControlName = sourceControlName.toLocaleUpperCase();
            }

            if (this.dataFeedGridData !== null && this.dataFeedGridData !== undefined) {
                this.dataFeedGridData.forEach(e => {
                    if (e.SourceSystem === sourceControlName) {
                        if (e.FeedName === attributeValue.toLocaleUpperCase()) {
                            successCount += 1;
                        }
                    }

                });
                if (successCount > 0) {
                    this.isFeedNameAlreadyExists = true;
                }
                else {
                    this.isFeedNameAlreadyExists = false;
                }
            }
        }
        return this.isFeedNameAlreadyExists;
    }

    public checkForDuplicateFieldName(attributeValue: string, objectId: string): boolean {
        if (this.rowData.length > 0) {
            const duplicateFieldNames = this.rowData.filter((item) =>
                item.objectId !== objectId && item.fieldName.trim().toLocaleUpperCase() === attributeValue.trim().toLocaleUpperCase()
            );
            if (duplicateFieldNames.length > 0) {
                this.isNameAlreadyExists = true;
            }
            else {
                this.isNameAlreadyExists = false;
            }
        } else {
            this.isNameAlreadyExists = false;
        }
        return this.isNameAlreadyExists;
    }

    public checkForNumericCharacters(attributeValue: string) {
        const numericCharacters = /^[0-9 ]+$/;
        if (numericCharacters.test(attributeValue)) {
            this.containsOnlyNumbers = true;
        }
        else {
            this.containsOnlyNumbers = false;
        }
        return this.containsOnlyNumbers;
    }

    public allowEdit() {
        let editableRowsCount = 0;
        if (this.validGridOptions()) {
            this.gridApi.forEachNode((row) => {
                if (row.data.editable) {
                    editableRowsCount++;
                }
            });

            if (editableRowsCount === 0) {
                this.rowData.forEach((row) => {
                    if (row.editable) {
                        editableRowsCount++;
                    }
                });
            }
        }
        if (editableRowsCount > 0 && !this.hasNonSavedChanges) {
            this.hasNonSavedChanges = true;
            sendMessageCXOne({ hasNonSavedChanges: true });
            return false;
        }
        return true;
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("ETL_Data_Feed_Create", key, defaultValue);
        return baseString;
    }

    public invalidFieldData(fieldGrid: ICellRendererParams) {
        let invalidInput = false;
        if (this.validFieldGrid(fieldGrid)) {
            if (
                this.checkForEmpty(fieldGrid.node.data.fieldName) ||
                this.checkForEmpty(fieldGrid.node.data.dataType)
            ) {
                invalidInput = true;
            }
            if (
                this.checkForSpecialCharacters(fieldGrid.node.data.fieldName)
            ) {
                invalidInput = true;
            }
            if (
                this.checkForLengthFieldName(fieldGrid.node.data.fieldName)
            ) {
                invalidInput = true;
            }
        }
        return invalidInput;
    }

    public isEmptyObjectId(fieldGrid: ICellRendererParams) {
        if (this.validateInput(fieldGrid)) {
            return fieldGrid.node.data.objectId === '' ? true : false;
        }
    }

    public validateInput(inputParam: any) {
        return inputParam !== null && inputParam !== undefined ? true : false;
    }

    public insertNewField() {
        if (this.validGridOptions()) {
            this.addButtonDisable = true;
            this.addNewField = true;
            this.disableEdit(true, true);
            if (this.rowData.length === 0) {
                this.addNewField = false;
                this.insertField();
            }
        }
    }

    public insertField() {
        const newItem = this.getDataFieldEntity();
        newItem.editable = true;
        this.gridApi.applyTransaction({ add: [newItem], addIndex: this.gridApi.getDisplayedRowCount() });
        this.resetFieldsGrid();
        this.gridApi.ensureIndexVisible(this.gridApi.getDisplayedRowCount() - 1, 'bottom');
        this.isGridValid = false;
        this.gridApi.sizeColumnsToFit();
    }

    public resetFieldsGrid() {
        if (this.validGridOptions()) {
            this.gridApi.resetRowHeights();
            this.gridApi.refreshCells();
        }
    }

    public getDataFieldEntity() {
        return new DataFieldsEntity();
    }

    public checkForEmpty(attributeValue: string | number) {
        return attributeValue == undefined || attributeValue === '' ? true : false;
    }

    public checkForLengthFieldName(attributeValue: string) {
        let limitExceed = false;
        if (attributeValue.length > 50) {
            limitExceed = true;
        }
        return limitExceed;
    }

    public validFieldGrid(inputGrid): boolean {
        return !inputGrid ? false : true;
    }

    public validGridOptions(): boolean {
        return !this.gridOptions || !this.gridApi ? false : true;
    }

    public resetRowData(fieldGrid) {
        if (this.validFieldGrid(fieldGrid)) {
            fieldGrid.node.data.editable = false;
            this.resetFieldsGrid();
        }
    }

    public getDisplayedGridRowsCount() {
        let displayedRowCount = '';
        if (this.validGridOptions()) {
            displayedRowCount = this.gridRowsCountLabel(this.gridApi.getDisplayedRowCount());
        }
        return displayedRowCount;
    }

    public filterGridData(searchQuery) {
        this.searchInput = searchQuery;
        if (this.validateInput(searchQuery)) {
            this.addButtonDisable = true;
            if (searchQuery.length === 0) {
                this.filteredRowData = [...this.rowData];
                this.addButtonDisable = false;
            } else {
                this.filteredRowData = this.rowData.filter((item) => {
                    const fieldNameMatches = item.fieldName.toLocaleUpperCase().includes(searchQuery.toLocaleUpperCase());
                    let dataTypeMatches;
                    this.fieldTypeValues.forEach((fieldType) => {
                        if (item.dataType === fieldType.id) {
                            dataTypeMatches = fieldType.name.toLocaleUpperCase().includes(searchQuery.toLocaleUpperCase());
                        }
                    });
                    return fieldNameMatches || dataTypeMatches;

                });
            }
            this.fieldsDataRowCount = this.gridRowsCountLabel(this.filteredRowData.length);
            if (this.filteredRowData.length > 0) {
                this.isGridValid = true;
                this.isFormValid = true;
            }
            this.gridApi?.sizeColumnsToFit();
            this.gridApi?.refreshCells();
        }
    }

    public onBack() {
        if (this.feed_name.dirty || this.source_name.dirty || !this.allowEdit() || (!this.isEditable && this.rowData.length > 0)
            || (this.isEditable && !this.disableSaveBtn)) {
            const dialogData = {
                title: this.getLocalizationString("Cancel_Modal_Title", "Leave without saving?"),
                type: 'user-warning',
                message: '<div class="msg-line-1">' + this.getLocalizationString("Cancel_Modal_Lost_Changes", "All changes will be lost")
                    + '</div><div>' + this.getLocalizationString("Cancel_Modal_Discard_Changes",
                        "Are you sure you want to leave the page without saving?") + '</div>',
                primaryButtonText: this.getLocalizationString("Leave", "Leave"),
                closeButtonText: this.getLocalizationString("Stay", "Stay")
            };
            const ref = this._dialogService.open(MessageModalComponent, {
                width: '420px',
                height: '184px',
                styleClass: 'cx-one-ui modal-center',
                data: dialogData
            });

            ref.onClose.subscribe(x => {
                if (x) {
                    this.backAction.emit(null);
                    this.hasNonSavedChanges = false;
                    sendMessageCXOne({ hasNonSavedChanges: false });
                }
            });
        } else {
            this.backAction.emit(null);
            this.hasNonSavedChanges = false;
            sendMessageCXOne({ hasNonSavedChanges: false });
        }
    }

    public getDynamicGridClass() {
        if (this.invalidFieldNameFormat && this.primaryKeyNotSelected)
            return 'fields-grid-err-2';
        else if (this.invalidFieldNameFormat || this.primaryKeyNotSelected)
            return 'fields-grid-err';
        else return 'fields-grid';
    }

    public onCreateSource(event) {
        if (!this.checkForEmpty(event.text)) {
            const newItems = [{ value: event.text, name: event.text }];
            this.sourceNameList = this.sourceNameList.concat(newItems);
            this.setSource({ selected: newItems[0] });
        }
    }

    public setSource(event) {
        if (event) {
            this.dataSourceName = event.selected.name;
            this.source_name.setValue({ sourceName: event.selected.name });
            this.selectedSource = {
                value: event.selected.name,
                name: event.selected.name
            };
        }
        this.validateForm();
    }
}
